/**
 * ProfilePhotoGallery Component
 * Displays up to 3 profile photos
 * Responsive: 3-column grid on all viewports (mobile carousel planned)
 */

import { ChevronLeft, ChevronRight } from "lucide-react"
import { useState } from "react"
import { Dialog, DialogContent } from "@/components/ui/dialog"
import { cn } from "@/lib"

interface ProfilePhotoGalleryProps {
  photos: string[]
  className?: string
}

export function ProfilePhotoGallery({ photos, className }: ProfilePhotoGalleryProps) {
  const slots = ["slot-1", "slot-2", "slot-3"] as const
  const validPhotos = photos.filter(photo => photo)
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null)

  const openPreview = (index: number) => setSelectedIndex(index)
  const closePreview = () => setSelectedIndex(null)
  const goPrev = () => {
    if (selectedIndex !== null && selectedIndex > 0) {
      setSelectedIndex(selectedIndex - 1)
    }
  }
  const goNext = () => {
    if (selectedIndex !== null && selectedIndex < validPhotos.length - 1) {
      setSelectedIndex(selectedIndex + 1)
    }
  }

  return (
    <div className={cn(className)}>
      <Dialog open={selectedIndex !== null} onOpenChange={open => !open && closePreview()}>
        <DialogContent
          className="w-[1440px] max-w-[calc(100%-2rem)] p-0 bg-black/90 border-none"
          showCloseButton={false}
        >
          {selectedIndex !== null && validPhotos[selectedIndex] && (
            <img
              src={validPhotos[selectedIndex]}
              alt={`Profile ${selectedIndex + 1}`}
              className="w-full h-auto max-h-[100vh] object-contain"
            />
          )}

          {selectedIndex !== null && validPhotos.length > 1 && (
            <>
              {selectedIndex > 0 && (
                <button
                  type="button"
                  onClick={goPrev}
                  className="absolute left-4 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/10 border border-white/20 flex items-center justify-center hover:bg-white/20 transition-colors cursor-pointer z-50"
                  aria-label="Previous image"
                >
                  <ChevronLeft className="w-5 h-5 text-white" />
                </button>
              )}
              {selectedIndex < validPhotos.length - 1 && (
                <button
                  type="button"
                  onClick={goNext}
                  className="absolute right-4 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/10 border border-white/20 flex items-center justify-center hover:bg-white/20 transition-colors cursor-pointer z-50"
                  aria-label="Next image"
                >
                  <ChevronRight className="w-5 h-5 text-white" />
                </button>
              )}
            </>
          )}

          {selectedIndex !== null && (
            <p className="absolute bottom-4 left-1/2 -translate-x-1/2 text-white/60 text-sm">
              {selectedIndex + 1} / {validPhotos.length}
            </p>
          )}
        </DialogContent>
      </Dialog>

      {/* Mobile: Carousel (≤ 768px) */}
      {/* {validPhotos.length > 0 && (
        <Carousel plugins={[WheelGesturesPlugin()]} className="block md:hidden">
          <CarouselContent>
            {validPhotos.map((photo, index) => (
              <CarouselItem key={index}>
                <button
                  type="button"
                  className="w-full aspect-square rounded-[20px] bg-card overflow-hidden cursor-pointer"
                  onClick={() => openPreview(index)}
                  aria-label={`View photo ${index + 1}`}
                >
                  <img
                    src={photo}
                    alt={`Profile ${index + 1}`}
                    className="w-full h-full object-cover"
                  />
                </button>
              </CarouselItem>
            ))}
          </CarouselContent>
          <CarouselPrevious />
          <CarouselNext />
        </Carousel>
      )} */}

      {/* Desktop: Grid (> 768px) */}
      <div className="grid grid-cols-3 gap-2.5 md:gap-5">
        {slots.map((slotId, index) => {
          const photo = photos[index] || ""
          return (
            <div key={slotId} className="aspect-square rounded-[20px] bg-card overflow-hidden">
              {photo ? (
                <button
                  type="button"
                  className="w-full h-full cursor-pointer"
                  onClick={() => openPreview(index)}
                  aria-label={`View photo ${index + 1}`}
                >
                  <img
                    src={photo}
                    alt={`Profile ${index + 1}`}
                    className="w-full h-full object-cover"
                  />
                </button>
              ) : (
                <div className="w-full h-full" />
              )}
            </div>
          )
        })}
      </div>
    </div>
  )
}
